import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, MailCheck } from 'lucide-react';
import { AuthShell } from '@/components/AuthShell';

export function ForgotPassword() {
  const navigate = useNavigate();
  const { t } = useTranslation('auth');
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!email || isLoading) return;
    setIsLoading(true);
    try {
      await fetch('/internal/v1/auth/forgot-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
    } catch {
      // Same generic outcome either way (no enumeration)
    }
    setSent(true);
    setIsLoading(false);
  }

  return (
    <AuthShell>
      <div>
        <h2 className="text-xl font-bold text-white">{t('forgot.title')}</h2>
        <p className="text-white/60 mt-1 text-sm">{t('forgot.subtitle')}</p>
      </div>

      {sent ? (
        <div className="space-y-5">
          <div className="flex items-start gap-3 px-4 py-3.5 rounded-lg bg-emerald-500/15 border border-emerald-400/25">
            <MailCheck className="w-5 h-5 text-emerald-300 shrink-0 mt-0.5" />
            <p className="text-sm text-emerald-100">{t('forgot.sentNote')}</p>
          </div>
          <button
            type="button"
            onClick={() => void navigate('/login')}
            className="w-full flex items-center justify-center gap-1.5 text-sm font-medium text-white/70 hover:text-white transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            {t('forgot.backToLogin')}
          </button>
        </div>
      ) : (
        <form onSubmit={(e) => void handleSubmit(e)} className="space-y-4">
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-white/80 mb-1.5">
              {t('login.emailLabel')}
            </label>
            <input
              type="email"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={t('login.emailPlaceholder')}
              disabled={isLoading}
              autoComplete="email"
              className="glass-input w-full px-4 py-2.5 rounded-lg text-sm text-white placeholder:text-white/50 focus:outline-none focus:border-brand-rose focus:bg-white/[0.22] focus:ring-[3px] focus:ring-brand-rose/25 disabled:opacity-50"
            />
          </div>

          <button
            type="submit"
            disabled={isLoading || !email}
            className="w-full py-2.5 rounded-lg text-white font-semibold text-sm shadow-lg bg-gradient-to-r from-brand-rose to-brand-orange hover:from-brand-purple hover:to-brand-rose transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {isLoading && <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />}
            {isLoading ? t('forgot.submitting') : t('forgot.submit')}
          </button>

          <button
            type="button"
            onClick={() => void navigate('/login')}
            className="w-full flex items-center justify-center gap-1.5 text-xs font-medium text-white/60 hover:text-white transition-colors"
          >
            <ArrowLeft className="w-3.5 h-3.5" />
            {t('forgot.backToLogin')}
          </button>
        </form>
      )}
    </AuthShell>
  );
}
